import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { change, formValueSelector } from 'redux-form'
import PropTypes from 'prop-types'
import Dropzone from 'react-dropzone'
import * as uploadTypes from 'actions/uploader/types';

const UploaderField = ({
  multipleUpload, form, name, groupClassName, label, labelClassName,
  acceptedFiles = 'image/gif, image/jpeg, image/png', dispatch, defaultImages, maxUpload = 7
}) => {
  const [processing, setProcessing] = useState(false)
  const [uploadedFiles, setUploadedFiles] = useState([])
  const initAction = { action: 'add', multiple: true }
  const [uploadAction, setUploadAction] = useState(initAction)

  const handleFileChange = async (files) => {
    const action = uploadAction.action
    const max = action === 'edit' ? 1 : maxUpload - uploadedFiles.length
    setProcessing(true)
    const results = await multipleUpload({ files, max });
    const images = results.map(result => {
      return {
        path: result.path,
        name: result.name,
        order: action === 'add' ? result.index + uploadedFiles.length + 1 : uploadAction.index + 1
      }
    })
    if (action === 'add') {
      setUploadedFiles([...uploadedFiles, ...images])
    } else {
      uploadedFiles[uploadAction.index] = images[0]
      setUploadedFiles([...uploadedFiles])
    }
    setProcessing(false)
    setUploadAction(initAction)
  }

  const deleteByIndex = (index) => {
    uploadedFiles.splice(index, 1)
    const newImages = uploadedFiles.map((img, i) => ({ ...img, order: i + 1 }))
    setUploadedFiles([...newImages])
  }

  const download = (url, filename) => {
    fetch(url.replace(/^http:/, 'https:')).then((t) => {
        return t.blob().then((b) => {
          const a = document.createElement('a')
          a.href = URL.createObjectURL(b)
          a.setAttribute('download', filename)
          a.click()
        }
      )
    })
  }

  useEffect(() => {
    if (defaultImages && defaultImages.length) setUploadedFiles(defaultImages)
  }, [defaultImages])

  useEffect(() => {
    if (maxUpload === 1) {
      dispatch(change(form, name, uploadedFiles[0]))
    } else {
      dispatch(change(form, name, uploadedFiles))
    }
  }, [uploadedFiles])

  return (
    <div className={`flex flex-col ${groupClassName}`}>
      {label && <label className={labelClassName}>{label}</label>}
      <Dropzone
        disabled={processing}
        accept={acceptedFiles}
        onDrop={files => handleFileChange(files)}
      >
        {({ getRootProps, getInputProps, isDragReject }) => (
          <section>
            <div
              className="border-dashed border-4 border-secondary-lightgray border-lg py-5 text-center cursor-pointer"
              {...getRootProps()}
            >
              <input {...getInputProps()} />
              {isDragReject && (
                <div className="text-action-danger pointer-events-none">
                  <i className="fa fa-3x fa-ban" aria-hidden="true"></i>
                  <p>{`The file format doesn't seem to be allowed.`}</p>
                </div>
              )}
              {!isDragReject && !processing && (
                <div className="text-primary-lightgray pointer-events-none">
                  <i className="fa fa-3x fa-cloud-upload" aria-hidden="true"></i>
                  <p>{`Drag your files here or simply click in this box to upload`}</p>
                </div>
              )}
              {processing && <div>
                <i className="fa fa-spinner fa-spin fa fa-spinner fa-spin text-primary-lightgray text-5xl"/>
              </div>}
            </div>
          </section>
        )}
      </Dropzone>
      <ul className="my-2">
        {uploadedFiles.map((file, index) => (
          <li className="flex my-2 py-1 px-2 bg-secondary-lightgray justify-around items-center" key={index}>
            <i className="fa fa-file-o mx-2 text-primary-lightgray" aria-hidden="true"></i>
            <small className="flex-1 text-primary-orange">{file.name}</small>
            <i className="fa fa-times px-2 cursor-pointer text-primary-lightgray" aria-hidden="true" onClick={() => deleteByIndex(index)}></i>
            <button type="button"className="flex justify-center items-center" onClick={() => download(file.path, file.name)}>
              <i className="fa fa-download text-primary-lightgray" aria-hidden="true"></i>
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}

UploaderField.propTypes = {
  multipleUpload: PropTypes.func,
  src: PropTypes.string,
  form: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  acceptedFiles: PropTypes.string,
  groupClassName: PropTypes.string,
  label: PropTypes.string,
  labelClassName: PropTypes.string,
  uploadedFiles: PropTypes.array,
  dispatch: PropTypes.func,
  defaultImages: PropTypes.array,
  maxUpload: PropTypes.number
}

const mapStateToProps = ({ merchant, ...state }, { form, name }) => {
  const selector = formValueSelector(form)
  return {
    defaultImages: selector(state, name) || [],
  }
}

const mapDispatchToProps = dispatch => ({
  multipleUpload: (payload) => new Promise((resolve, reject) => {
    dispatch({ type: uploadTypes.MULTIPLE_UPLOAD, payload, resolve, reject });
  }),
  dispatch
})

export default connect(mapStateToProps, mapDispatchToProps)(UploaderField)
